<template>
  <div class="kiosk-order-v2">
    <div class="kiosk-order-layout">
      <!-- Cart Backdrop -->
      <div
        v-if="cartExpanded"
        class="kiosk-cart-backdrop"
        @click="cartExpanded = false"
      />

      <!-- Left Sidebar - Cart Drawer -->
      <CartDrawerV2
        :expanded="cartExpanded"
        :animating="cartAnimating"
        :items="orderItems.filter((i) => !i.void_status)"
        :total-item-count="totalItemCount"
        :total="currentOrderSum"
        :subtotal="currentOrder.subtotal"
        :tax-total="currentOrder.tax_total"
        :grand-total="currentOrder.grand_total"
        :currency="currency"
        :checkout-disabled="!isPaymentTypesAvailable || orderItems.length === 0"
        @toggle-expand="cartExpanded = !cartExpanded"
        @void-item="voidItem"
        @show-notes="showNotesModal"
        @decrease-qty="decreaseQuantity"
        @increase-qty="increaseQuantity"
        @checkout="goToCheckout"
      />

      <!-- Main Content Area -->
      <div
        class="kiosk-order-main"
        :class="{ 'with-cart': orderItems.length > 0 }"
      >
        <CategoriesSidebarV2
          :categories="displayCategories"
          :current-category="currentCategory"
          :primary-color="primaryColor"
          :logo="logoSource"
          :has-logo="hasStoreLogo"
          :store-name="store && store.name"
          :initial="storeInitial"
          @select-category="selectCategory"
          @logo-click="selectCategory('all')"
        />

        <ProductsGridV2
          :products="paginatedProducts"
          :title="store && store.name ? store.name : 'Ordering'"
          :dining-options="activeDiningOptions"
          :current-dining-option="currentOrder.dining_option_type"
          :currency="currency"
          :adding-id="addingItemId"
          :added-id="addedItemId"
          :has-description="shouldShowProductInfo"
          @cancel-order="cancelOrder"
          @select-dining-option="selectDiningOption"
          @open-builder="openBuilderScreen"
          @add-directly="addItemDirectly"
          @show-info="(p) => $bvModal.show('modal-show-info-' + p.id)"
        />
      </div>
    </div>

    <!-- Modifiers Sidebar -->
    <ModifiersSidebarV2
      :show="modifiersShow"
      :title="
        modifiers.product
          ? getProductDisplayName(modifiers.product)
          : modifiers.productName
      "
      :groups="modifiers.modifierGroupsAtCurrentItem"
      :currency="currency"
      :get-selected-count="countSelectedModifiers"
      :get-rules="modifierGroupRules"
      :is-selected="isModifierSelected"
      @save="modifiersSave"
      @delete="deleteItemFromModifiers"
      @toggle="toggleModifierForItem"
    />

    <!-- Info Modals -->
    <ProductInfoModal
      v-for="product in paginatedProducts"
      :key="product.id"
      :product="product"
      :transform-color="transformColor"
      :display-name="getProductDisplayName(product)"
    />

    <!-- Core Modals -->
    <SimpleInfoModal :id="'modal-product-not-found'">
      <template v-slot:title>
        Not Found
      </template>
      <template v-slot:info>
        No product or order found with that search.
        {{ $t("modal-info.try-again") }}
      </template>
    </SimpleInfoModal>
    <ModalItemNotesV2 @saveNote="saveNote($event)" />
    <BuilderScreenV2
      :show="builderScreenShow"
      :product="builderProduct"
      :currency="currency"
      :vertical-mode="kioskVerticalModifiers"
      @close="closeBuilderScreen"
      @add-to-cart="handleBuilderAddToCart"
    />
    <ModalPriceAdjustV2
      :show="priceAdjustShow"
      @close="priceAdjustShow = false"
      @savePrice="savePrice($event)"
    />
    <scale-loader
      :loading="isLoad"
      :color="'#2563EB'"
    />
    <ModalError
      :id="errorModal.modalId"
      :error="errorModal.errorBody"
      @hide-modal="errorModalCallBack"
    />
  </div>
</template>

<script>
import Order from "../Order/StandardOrder.vue";
import ModalItemNotesV2 from "../../components/ModalItemNotesV2.vue";
import BuilderScreenV2 from "./BuilderScreenV2.vue";
import ProductInfoModal from "../../components/ProductInfoModal.vue";
import { shouldShowProductDescription } from "@/helpers/productDescriptionHelper";
import { createCancelOrderModalV2Template } from "@/helpers/modalTemplatesHelper";
import ModalPriceAdjustV2 from "./ModalPriceAdjustV2.vue";
import CartDrawerV2 from "./components/CartDrawerV2.vue";
import CategoriesSidebarV2 from "./components/CategoriesSidebarV2.vue";
import ProductsGridV2 from "./components/ProductsGridV2.vue";
import ModifiersSidebarV2 from "./components/ModifiersSidebarV2.vue";
import SimpleInfoModal from "@/components/SimpleInfoModal";
import ModalError from "@/modals/ModalError";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";

export default {
  name: "KioskOrderV2",
  extends: Order,
  components: {
    ModalItemNotesV2,
    BuilderScreenV2,
    ProductInfoModal,
    ModalPriceAdjustV2,
    CartDrawerV2,
    CategoriesSidebarV2,
    ProductsGridV2,
    ModifiersSidebarV2,
    SimpleInfoModal,
    ModalError,
    ScaleLoader,
  },
  data() {
    return {
      cartExpanded: false,
      builderScreenShow: false,
      builderProduct: null,
      addingItemId: null,
      addedItemId: null,
      priceAdjustShow: false,
      cartAnimating: false,
    };
  },
  mounted() {
    this.$root.$on("kiosk:idle-cancel", this.handleIdleCancel);
  },
  beforeDestroy() {
    this.$root.$off("kiosk:idle-cancel", this.handleIdleCancel);
  },
  watch: {
    currentCategory: {
      handler() {
        this.applyKioskProductFilter();
      },
      immediate: true,
    },
    /** Re-filter when 86 status changes (local or from other terminals). */
    "$store.state.products86d.productIds": {
      deep: true,
      handler() {
        this.applyKioskProductFilter();
      },
    },
  },
  computed: {
    kioskVerticalModifiers() {
      return this.$store.getters["config/kioskVerticalModifiers"];
    },
    store() {
      return this.$store.getters["config/store"];
    },
    isWhitelabelEnabled() {
      return this.$store.getters["config/isWhitelabelEnabled"];
    },
    primaryColor() {
      return this.$store.getters["config/primaryColor"];
    },
    whitelabelLogo() {
      return this.$store.getters["config/whitelabelLogo"];
    },
    logoSource() {
      return this.isWhitelabelEnabled && this.whitelabelLogo
        ? this.whitelabelLogo
        : (this.store && this.store.logo) ||
            require("@/assets/images/logo.svg");
    },
    hasStoreLogo() {
      if (this.isWhitelabelEnabled && this.whitelabelLogo) {
        return true;
      }
      // Check if store has a logo that's not the default
      if (this.store && this.store.logo) {
        // If logo is a URL or path (not the default logo.svg), show it
        return !this.store.logo.includes("logo.svg");
      }
      return false;
    },
    storeInitial() {
      if (!this.store || !this.store.name) return "M";
      return this.store.name.charAt(0).toUpperCase();
    },
    totalItemCount() {
      return this.orderItems
        .filter((i) => !i.void_status)
        .reduce((sum, item) => sum + item.quantity, 0);
    },
    activeDiningOptions() {
      if (!this.terminal || !this.terminal.dining_options) return [];
      return this.terminal.dining_options.filter((option) => option.active);
    },
  },
  methods: {
    async handleIdleCancel() {
      const order = this.currentOrder;
      const items = order && Array.isArray(order.items) ? order.items : [];
      const hasRealOrder =
        order && ((order.id && order.id > 0) || (order.local_id && order.local_id > 0) || items.length > 0);
      if (!hasRealOrder) return;
      try {
        await this.cancelOrderProcessing({ skipNavigation: true });
      } catch (e) {
        console.error("[KioskIdle] KioskOrderV2 idle cancel failed", e);
      }
    },
    cancelOrderModalPromise(customTemplate = null) {
      const h = this.$createElement;
      const messageVNode =
        customTemplate || createCancelOrderModalV2Template(h);

      return this.$bvModal.msgBoxConfirm([messageVNode], {
        title: [],
        modalClass: "cancel-order-modal-v2-wrapper",
        hideHeader: true,
        footerClass: "cancel-order-modal-v2-footer",
        cancelVariant: "outline-secondary",
        okVariant: "dark",
        okTitle: "YES",
        cancelTitle: "NO",
        noFade: true,
        centered: true,
      });
    },
    applyKioskProductFilter() {
      const value = this.currentCategory;
      if (!this.products) return;

      this.filteredProductsByCategory = this.products
        .filter((item) => {
          if (
            item.id === -2 ||
            item.id === -3 ||
            item.name === "Points Redeemed"
          ) {
            return false;
          }

          if (this.isKioskMode && !item.order_kiosk) return false;
          if (this.isKioskMode && this.isProduct86d(item.id)) return false;

          if (value === "all") return true;

          return item.categories && item.categories.includes(value);
        })
        .sort(
          (a, b) =>
            (a.sorting || 0) - (b.sorting || 0) ||
            a.name.localeCompare(b.name),
        );

      this.pagination.pageCount = Math.ceil(
        this.filteredProductsByCategory.length / this.pagination.perPage,
      );
      this.paginatedProducts = [];
      this.pagination.page = 1;
      this.getProductListPaginated();
    },
    shouldShowProductInfo(product) {
      /**
       * Check if product description should be shown in info modal
       * Don't show if description contains "type": "alert"
       */
      if (!product) return false;
      return shouldShowProductDescription(product.description);
    },
    async goToCheckout() {
      if (!this.isPaymentTypesAvailable) return;
      this.isLoad = true;

      try {
        await this.sync(true, true);
      } catch (e) {
        console.error("Sync failed", e);
      } finally {
        this.isLoad = false;
      }

      const params = {
        id: this.currentOrder.id || this.currentOrder.local_id,
      };
      if (this.$route.params.referenceId) {
        params.referenceId = this.$route.params.referenceId;
      }

      await this.$router.push({
        name: "OrderCheckoutV2",
        params: params,
        query: { autoOpen: "true" },
      });
    },
    async voidItem(item) {
      // In kiosk mode, skip confirmation and remove directly
      await this.removeItemFromOrder(item.id, true);
    },
    async increaseQuantity(item) {
      const index = this.currentOrder.items.findIndex((i) => i.id === item.id);
      if (index !== -1) {
        await this.saveNewQuantity({
          index: index,
          newQtyStr: (item.quantity + 1).toString(),
        });
      }
    },
    async decreaseQuantity(item) {
      if (item.quantity > 1) {
        const index = this.currentOrder.items.findIndex(
          (i) => i.id === item.id,
        );
        if (index !== -1) {
          await this.saveNewQuantity({
            index: index,
            newQtyStr: (item.quantity - 1).toString(),
          });
        }
      }
    },
    async openBuilderScreen(product) {
      const hasModifiers =
        product.modifier_groups && product.modifier_groups.length > 0;

      if (!hasModifiers) {
        await this.addItemDirectly(product);
        return;
      }

      this.builderProduct = product;
      this.builderScreenShow = true;
    },
    async addItemDirectly(product) {
      if (this.addingItemId) return;

      this.addingItemId = product.id;
      try {
        await this.addItemToOrder(product);

        // Show success state
        this.addingItemId = null;
        this.addedItemId = product.id;
        this.triggerCartAnimation();

        // Reset after 1.5s
        setTimeout(() => {
          this.addedItemId = null;
        }, 1500);

        // Optional: still show the cart bump if desired
        // this.showCartBump();
      } catch (e) {
        console.error("Failed to add item", e);
        this.addingItemId = null;
      }
    },
    closeBuilderScreen() {
      this.builderScreenShow = false;
      this.builderProduct = null;
    },
    async handleBuilderAddToCart(data) {
      const { product, selections, quantity } = data;

      // Set quantity using selectedQuantityFromBar (used by addItemToOrder)
      this.selectedQuantityFromBar = quantity;

      // Prevent modifiers panel from opening automatically by temporarily clearing modifier_groups
      const originalModifierGroups = product.modifier_groups
        ? [...product.modifier_groups]
        : [];
      const hasModifiers = originalModifierGroups.length > 0;

      if (hasModifiers) {
        // Temporarily clear to prevent auto-opening modifiers panel
        product.modifier_groups = [];
      }

      // Add item to order (this will use selectedQuantityFromBar for quantity)
      await this.addItemToOrder(product);

      // Restore modifier_groups
      if (hasModifiers) {
        product.modifier_groups = originalModifierGroups;
      }

      // Wait for the item to be added
      await this.$nextTick();

      // Find the newly added item
      const newItemIndex = this.currentOrder.items.length - 1;
      const newItem = this.currentOrder.items[newItemIndex];

      if (newItem && hasModifiers && Object.keys(selections).length > 0) {
        // Set the modifiers context for savePredefinedModifiers
        this.modifiers.currentItemIndex = newItemIndex;
        this.modifiers.product = product;

        // Build modifier array in format expected by savePredefinedModifiers
        const modifierArray = [];
        Object.keys(selections).forEach((groupId) => {
          selections[groupId].forEach((option) => {
            modifierArray.push({
              oryginal: option.id,
            });
          });
        });

        // Apply modifiers using the existing method
        if (modifierArray.length > 0) {
          await this.savePredefinedModifiers(
            modifierArray,
            originalModifierGroups,
            newItemIndex,
          );
        }
      } else if (newItem && quantity > 1 && !hasModifiers) {
        // If no modifiers but quantity > 1, update quantity
        await this.saveNewQuantity({
          index: newItemIndex,
          newQtyStr: quantity.toString(),
        });
      }

      this.triggerCartAnimation();

      // Close builder screen
      this.closeBuilderScreen();
    },
    triggerCartAnimation() {
      this.cartAnimating = true;
      setTimeout(() => {
        this.cartAnimating = false;
      }, 500);
    },
    showPriceAdjustModal(item) {
      const index = this.currentOrder.items.findIndex(
        (i) => i.local_id === item.local_id,
      );
      if (index === -1) return;

      this.$store.commit("itemPriceAdjust/setItem", item);
      this.$store.commit("itemPriceAdjust/setIndex", index);
      this.priceAdjustShow = true;
    },
    async savePrice(event) {
      const { index, price } = event;
      this.currentOrder.items[index].price = price;
      this.currentOrder.items[index].price_base = price;
      this.currentOrder.items[index].price_before_tax = price;

      await this.countSubtotal();
      await this.sync(true, true);
      this.priceAdjustShow = false;
    },
  },
};
</script>

<style scoped>
/* Layout Styles for V2 */
.kiosk-order-v2 {
  height: 100vh;
  width: 100%;
  background-color: #f0f9ff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.kiosk-order-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.kiosk-cart-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 40;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Main Content Area */
.kiosk-order-main {
  display: flex;
  height: 100%;
  overflow: hidden;
  padding-bottom: 4.65rem;
}

/* Hide scrollbars */
.kiosk-order-v2 ::-webkit-scrollbar {
  display: none;
}

.kiosk-order-v2 {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
